'use client';
import { useState, useEffect } from 'react';
import { ButtonProps } from '@/ui-components/atoms/Button/Button';
import styles from './stickybar.module.scss';
import { Text } from '@/ui-components/atoms/Text/Text';
import cn from 'classnames';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { responsiveBreakpointsPx } from '@/constants';
import { CloseIcon } from '@/assets/icons/CloseIcon';

export type theme = 'white' | 'black' | 'blue';

export interface StickyBarProps {
    theme: theme;
    leadImage?: React.ReactElement<typeof HTMLImageElement>;
    headline: React.ReactNode;
    subhead?: React.ReactNode;
    legal?: React.ReactNode;
    qrcode?: React.ReactElement<typeof HTMLImageElement>;
    qrMobileLink?: React.ReactElement<ButtonProps>;
    ctas?: React.ReactElement<ButtonProps>[];
    ctaCaption?: React.ReactNode;
    className?: string;
    imageMobile?: boolean;
    dismissible?: boolean;
}

export function StickyBar({
    theme = 'white',
    leadImage,
    headline,
    subhead,
    legal,
    qrcode,
    qrMobileLink,
    ctas = [],
    ctaCaption,
    className = '',
    imageMobile,
    dismissible = true,
}: StickyBarProps) {
    const [isDismissed, setIsDismissed] = useState(false);

    const containerClassNames = cn(styles.container, styles[theme], className, {
        [styles.dismissible]: dismissible,
    });

    useEffect(() => {
        if (isDismissed) {
            document.body.classList.remove('stickyBarStick');
        }
    }, [isDismissed]);

    const isViewportDesktop = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);
    const gridStyles = cn(styles.bar, {
        [styles.dismissIconStyles]: dismissible,
    });

    return (
        <div className={containerClassNames}>
            <Grid container className={gridStyles}>
                <div className={styles.leftBar}>
                    {leadImage && (isViewportDesktop || (!isViewportDesktop && imageMobile)) && (
                        <div className={styles.leadImage}>{leadImage}</div>
                    )}

                    <div className={styles.text}>
                        <Text tag="div" variant="h5" className={styles.headline}>
                            {headline}
                        </Text>

                        {subhead && (
                            <Text tag="div" variant="text-medium" className={styles.subhead}>
                                {subhead}
                            </Text>
                        )}

                        {legal && (
                            <Text tag="div" variant="text-legal" className={styles.legal}>
                                {legal}
                            </Text>
                        )}
                    </div>
                </div>

                {(qrcode || ctas.length > 0) && (
                    <div className={styles.rightBar}>
                        {qrcode && <div className={styles.qr}>{qrcode}</div>}

                        {ctas.length > 0 && (
                            <div className={styles.ctagroup}>
                                <div className={styles.cta}>
                                    {ctas}

                                    {qrMobileLink && <div className={styles.qrlink}>{qrMobileLink}</div>}
                                </div>

                                {ctaCaption && (
                                    <Text tag="div" variant="text-legal" className={styles.ctacaption}>
                                        {ctaCaption}
                                    </Text>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Grid>
            {dismissible && (
                <div className={styles.closeWrapper}>
                    <button
                        className={styles.clickableArea}
                        aria-label="Dismiss banner"
                        onClick={() => {
                            sessionStorage.setItem('stickyCtaDismissed', '1');
                            setIsDismissed(true);
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            )}
        </div>
    );
}
